import styled from '@emotion/styled';
import React, { useState, useEffect, useCallback } from 'react';

import { palette } from '~/utils/colours';

const Container = styled.div`
  display: inline-block;
  padding: 2px;
  background-color: ${({ enabled }: SwitchSharedProps) =>
    enabled ? palette.tealishTwo : palette.lightGrey};
  width: 38px;
  height: 20px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Head = styled.div`
  background-color: white;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  margin-left: ${({ enabled }: SwitchSharedProps) => (enabled ? '18px' : '0')};
  transition: margin 0.3s;
`;

interface SwitchSharedProps {
  enabled: boolean;
}

export interface SwitchProps {
  value?: boolean;
  defaultValue?: boolean;
  onChange?: (newValue: boolean) => void;
  onClick?: (newValue: boolean, currentValue: boolean) => void;
  style?: React.CSSProperties;
}

interface SwitchInnerProps {
  enabled: boolean;
  onClick: (newValue: boolean, oldValue: boolean) => void;
  style?: React.CSSProperties;
}

function SwitchInner({ enabled, onClick, style }: SwitchInnerProps) {
  return (
    <Container
      enabled={enabled}
      onClick={() => onClick(!enabled, enabled)}
      style={style}
    >
      <Head enabled={enabled} />
    </Container>
  );
}

type SwitchControlledProps = Required<Pick<SwitchProps, 'value'>> &
  Pick<SwitchProps, 'style' | 'onClick'>;

function SwitchControlled({ value, onClick, style }: SwitchControlledProps) {
  return (
    <SwitchInner
      enabled={value}
      onClick={() => onClick?.(!value, value)}
      style={style}
    />
  );
}

type SwitchUncontrolledProps = Pick<
  SwitchProps,
  'defaultValue' | 'onChange' | 'style'
>;

function SwitchUncontrolled({
  defaultValue,
  onChange,
  style,
}: SwitchUncontrolledProps) {
  const [enabled, setEnabled] = useState<boolean>(defaultValue ?? false);
  const [hasClicked, setHasClicked] = useState(false);

  const handleClick = useCallback(() => {
    setHasClicked(true);
    setEnabled(!enabled);
  }, [hasClicked, enabled]);

  useEffect(() => {
    if (hasClicked) {
      onChange?.(enabled);
    }
  }, [enabled]);

  return <SwitchInner enabled={enabled} onClick={handleClick} style={style} />;
}

export default function Switch({
  value,
  defaultValue,
  onChange,
  onClick,
  style,
}: SwitchProps) {
  const [isControlled] = useState(value != null);

  if (isControlled) {
    return (
      <SwitchControlled
        value={value ?? false}
        onClick={onClick}
        style={style}
      />
    );
  }

  return (
    <SwitchUncontrolled
      defaultValue={defaultValue}
      onChange={onChange}
      style={style}
    />
  );
}
